// agentQuestionsAdaptatives.js
// Génère 5 questions adaptatives en fonction de la solution sélectionnée
// et du contexte projet réel.
// Strictement read-only sur diagnosticBackend. Ne recalcule pas le classement.

const NOMS_SOLUTION = {
  pieux_beton:             "Pieux Béton",
  pieux_fores:             "Pieux Forés",
  semelles_superficielles: "Semelles Superficielles",
  radier_general:          "Radier Général",
  micropieux:              "Micropieux",
  longrines:               "Longrines",
  palplanches:             "Rideau de Palplanches",
  massif_renforce:         "Massif Renforcé",
  stabilite_coins_roche:   "Stabilité des Coins de Roche",
  pieux_secants:           "Paroi de Pieux Sécants",
  paroi_moldee:            "Paroi Moulée",
  ecran_ancre:             "Écran Ancré",
  ecran_berlinois:         "Écran Berlinois",
  mur_cantilever:          "Mur Cantilever",
  gabions:                 "Gabions",
  colonnes_sol_ciment:     "Colonnes Sol-Ciment",
  colonnes_ballastees:     "Colonnes Ballastées",
  amelioration_sol:        "Amélioration de Sol",
};

const getNom = (id) =>
  NOMS_SOLUTION[id] || (id || "").replace(/_/g, " ");

const extraireRegles = (data) => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (typeof data === "string") return [data];
  if (Array.isArray(data.regles))  return data.regles;
  if (Array.isArray(data.motifs))  return data.motifs;
  if (Array.isArray(data.raisons)) return data.raisons;
  if (typeof data.regle  === "string") return [data.regle];
  if (typeof data.motif  === "string") return [data.motif];
  if (typeof data.raison === "string") return [data.raison];
  return Object.keys(data).filter(k => k !== "id");
};

function chercherSolution(solutionId, diag) {
  // 1. Chercher dans le classement
  const classement = diag?.adequation?.classement || [];
  const item = classement.find(s => s.id === solutionId);
  if (item) {
    const data    = diag?.adequation?.solutions?.[solutionId] || {};
    const modules = diag?.modules_geo5?.par_solution_eligible?.[solutionId] || [];
    return {
      id:       solutionId,
      rang:     item.rang,
      niveau:   item.niveau || data.niveau,
      nom:      data.nom || getNom(solutionId),
      motifs:   data.motifs_positifs || [],
      reserves: data.avertissements  || [],
      regles:   [],
      modules,
      statut:   "classee",
    };
  }

  // 2. Chercher dans les autres évaluées (adequation.solutions, pas dans classement)
  const data = diag?.adequation?.solutions?.[solutionId];
  if (data) {
    const modules = diag?.modules_geo5?.par_solution_eligible?.[solutionId] || [];
    return {
      id:       solutionId,
      rang:     null,
      niveau:   data.niveau || "PEU_ADAPTÉE",
      nom:      data.nom || getNom(solutionId),
      motifs:   data.motifs_positifs || [],
      reserves: data.avertissements  || [],
      regles:   [],
      modules,
      statut:   "evaluee",
    };
  }

  // 3. Chercher dans les solutions écartées (eligibilite.exclues est un objet)
  const exclues    = diag?.eligibilite?.exclues || {};
  const exclueData = exclues[solutionId];
  if (exclueData !== undefined) {
    const reglesDirectes = extraireRegles(exclueData);
    const declenchees    = diag?.eligibilite?.regles_declenchees || {};
    const reglesDecl     = Array.isArray(declenchees[solutionId])
      ? declenchees[solutionId]
      : [];
    const toutesRegles = [...new Set([...reglesDirectes, ...reglesDecl])];
    return {
      id:       solutionId,
      rang:     null,
      niveau:   "ÉCARTÉE",
      nom:      getNom(solutionId),
      motifs:   [],
      reserves: [],
      regles:   toutesRegles,
      modules:  [],
      statut:   "exclue",
    };
  }

  return null;
}

function getRang1(diag) {
  const classement = diag?.adequation?.classement || [];
  const r1 = classement[0];
  if (!r1) return null;
  const data = diag?.adequation?.solutions?.[r1.id] || {};
  return {
    id:      r1.id,
    nom:     data.nom || getNom(r1.id),
    modules: diag?.modules_geo5?.par_solution_eligible?.[r1.id] || [],
  };
}

function getCtx(donneesProjet) {
  const typeSol    = donneesProjet?.typeSol    || "sol";
  const h          = donneesProjet?.hauteur    || "?";
  const zone       = donneesProjet?.zoneSismique || "?";
  const nappe      = donneesProjet?.nappe;
  const nappeLabel = (nappe && nappe !== "absente" && nappe !== "nulle" && nappe !== "basse")
    ? nappe : null;
  return { typeSol, h, zone, nappe, nappeLabel };
}

// Retourne le motif tel quel s'il est court (≤100 chars), chaîne vide sinon.
// Empêche les descriptions backend multi-paragraphes d'être injectées dans les questions.
const formatMotifCourtPourQuestion = (motif) => {
  if (!motif || typeof motif !== 'string') return '';
  return motif.length <= 100 ? motif : '';
};

// ─── Helpers de niveau ──────────────────────────────────────────────────────

function questionsTresAdaptee(sol, donneesProjet, diag) {
  const { typeSol, h, zone, nappeLabel } = getCtx(donneesProjet);
  const mods = sol.modules.slice(0, 3).join(", ") || "Fondations, Tassements";

  return [
    {
      id:       "justification",
      category: "justification",
      texte:    `Pourquoi ${sol.nom} est-elle classée "Très adaptée" pour ce projet sur ${typeSol} en Zone ${zone} — principaux critères du moteur expert ?`,
    },
    {
      id:       "modules_geo5",
      category: "modules",
      texte:    `Quels modules GEO5 sont indispensables pour dimensionner ${sol.nom} sur ${typeSol} en Zone ${zone} — ${mods} ?`,
    },
    {
      id:       "verifs_ec7",
      category: "dimensionnement",
      texte:    `Quelles vérifications EC7 DA2* sont à effectuer pour ${sol.nom} sur ${typeSol} H=${h}m — ELU portance, ELS tassement ?`,
    },
    {
      id:       "parametres_sol",
      category: "contexte",
      texte:    `Quels paramètres de sol spécifiques à saisir dans GEO5 pour ${typeSol} avec ${sol.nom} selon NF P94-261 §4.2 ?`,
    },
    {
      id:       nappeLabel ? "nappe" : "sf_securite",
      category: nappeLabel ? "contexte" : "dimensionnement",
      texte:    nappeLabel
        ? `Impact de la nappe (${nappeLabel}) sur le dimensionnement de ${sol.nom} — sous-pressions et drainage EC7 §6.7 ?`
        : `Quels facteurs de sécurité minimaux EC7 DA2* pour ${sol.nom} sur ${typeSol} en Zone ${zone} — ELU glissement et renversement ?`,
    },
  ];
}

function questionsAdaptee(sol, donneesProjet, diag) {
  const { typeSol, h, zone, nappeLabel } = getCtx(donneesProjet);
  const rang1     = getRang1(diag);
  const rang1Nom  = rang1?.nom || "la solution principale";
  const mods      = sol.modules.slice(0, 2).join(", ") || "Fondations";

  return [
    {
      id:       "vs_rang1",
      category: "comparaison",
      texte:    `Pourquoi ${sol.nom} est-elle classée après ${rang1Nom} pour ce projet sur ${typeSol} — conditions qui inverseraient le classement ?`,
    },
    {
      id:       "conditions_optimales",
      category: "justification",
      texte:    `Dans quelles conditions ${sol.nom} deviendrait-elle préférable à ${rang1Nom} sur ${typeSol} en Zone ${zone} ?`,
    },
    {
      id:       "modules_geo5",
      category: "modules",
      texte:    `Quels modules GEO5 utiliser pour ${sol.nom} sur ${typeSol} H=${h}m — ${mods} — vérifications EC7 DA2* minimales ?`,
    },
    {
      id:       "verifs_sismique",
      category: "dimensionnement",
      texte:    `Vérifications sismiques EC8 pour ${sol.nom} sur ${typeSol} en Zone ${zone} — NF EN 1998-5 ?`,
    },
    {
      id:       nappeLabel ? "nappe" : "cout_delai",
      category: nappeLabel ? "contexte" : "comparaison",
      texte:    nappeLabel
        ? `Impact de la nappe (${nappeLabel}) sur ${sol.nom} — adaptation du dimensionnement EC7 §6.7 ?`
        : `${sol.nom} vs ${rang1Nom} : coût et délai de mise en œuvre comparés sur ${typeSol} H=${h}m ?`,
    },
  ];
}

function questionsAvecReserves(sol, donneesProjet, diag) {
  const { typeSol, h, zone } = getCtx(donneesProjet);
  const rang1    = getRang1(diag);
  const rang1Nom = rang1?.nom || "la solution principale";
  const reserveRaw  = sol.reserves[0] || '';
  const reserveCourt = formatMotifCourtPourQuestion(reserveRaw);
  const mods     = sol.modules.slice(0, 2).join(", ") || "Fondations";

  return [
    {
      id:       "reserves_detail",
      category: "reserves",
      texte:    reserveCourt
        ? `Quelles sont les réserves identifiées pour ${sol.nom} sur ${typeSol} H=${h}m — "${reserveCourt}" ?`
        : `Quelles réserves limitent l'usage de ${sol.nom} sur ce projet ?`,
    },
    {
      id:       "lever_reserves",
      category: "reserves",
      texte:    reserveCourt
        ? `Comment lever la réserve "${reserveCourt}" pour utiliser ${sol.nom} sans restriction sur ce projet ?`
        : `Comment lever les réserves identifiées pour utiliser ${sol.nom} sans restriction sur ce projet ?`,
    },
    {
      id:       "vs_principal",
      category: "comparaison",
      texte:    `Pourquoi préférer ${rang1Nom} à ${sol.nom} sur ${typeSol} en Zone ${zone} malgré la réserve ?`,
    },
    {
      id:       "conditions_levee",
      category: "justification",
      texte:    `Dans quelles conditions ${sol.nom} deviendrait-elle préférable à ${rang1Nom} une fois la réserve levée ?`,
    },
    {
      id:       "modules_geo5",
      category: "modules",
      texte:    `Quels modules GEO5 — ${mods} — pour dimensionner ${sol.nom} avec la réserve active sur ${typeSol} ?`,
    },
  ];
}

function questionsComplementaire(sol, donneesProjet, diag) {
  const { typeSol, h, zone } = getCtx(donneesProjet);
  const rang1     = getRang1(diag);
  const rang1Nom  = rang1?.nom || "la solution principale";
  const modulesUniques = Array.from(new Set([...sol.modules, ...(rang1?.modules || [])])).slice(0, 3);
  const modulesStr     = modulesUniques.join(" + ");

  return [
    {
      id:       "pourquoi_complementaire",
      category: "justification",
      texte:    `Pourquoi ${sol.nom} est-elle classée complémentaire et non principale pour ce projet sur ${typeSol} H=${h}m ?`,
    },
    {
      id:       "combinaison",
      category: "comparaison",
      texte:    `Quand utiliser ${sol.nom} en complément de ${rang1Nom} sur ${typeSol} en Zone ${zone} — conditions d'association ?`,
    },
    {
      id:       "role_structurel",
      category: "justification",
      texte:    `Quel est le rôle structurel de ${sol.nom} en complément de ${rang1Nom} — interface et phasage des travaux ?`,
    },
    {
      id:       "dimensionnement",
      category: "dimensionnement",
      texte:    `Comment dimensionner ${sol.nom} selon NF P94-261 en complément de ${rang1Nom} sur ${typeSol} H=${h}m ?`,
    },
    {
      id:       "modules_geo5",
      category: "modules",
      texte:    modulesStr
        ? `Modules GEO5 pour la combinaison ${sol.nom} + ${rang1Nom} : ${modulesStr} — vérifications à effectuer ?`
        : `Quels modules GEO5 pour ${sol.nom} en complément de ${rang1Nom} sur ${typeSol} ?`,
    },
  ];
}

function questionsPeuAdaptee(sol, donneesProjet, diag) {
  const { typeSol, h, zone } = getCtx(donneesProjet);
  const rang1    = getRang1(diag);
  const rang1Nom = rang1?.nom || "la solution recommandée";
  const facteurRaw  = sol.reserves[0] || sol.motifs[0] || null;
  const facteurCourt = facteurRaw ? formatMotifCourtPourQuestion(facteurRaw) : null;

  return [
    {
      id:       "pourquoi_peu_adaptee",
      category: "justification",
      texte:    `Pourquoi ${sol.nom} est-elle peu adaptée pour ce projet sur ${typeSol} H=${h}m en Zone ${zone} — facteurs limitants ?`,
    },
    {
      id:       "risques",
      category: "contexte",
      texte:    `Risques concrets si ${sol.nom} est utilisée malgré la classification peu adaptée sur ${typeSol} — ordre de grandeur ?`,
    },
    {
      id:       "vs_recommande",
      category: "comparaison",
      texte:    `Pourquoi ${rang1Nom} est-elle préférée à ${sol.nom} sur ${typeSol} en Zone ${zone} — comparaison SF et coût ?`,
    },
    {
      id:       "conditions_reevaluation",
      category: "justification",
      texte:    facteurCourt
        ? `Quelles modifications du projet permettraient de lever la limitation "${facteurCourt}" pour ${sol.nom} ?`
        : `Quelles modifications du projet permettraient de rendre ${sol.nom} acceptable dans ce contexte ?`,
    },
    {
      id:       "sf_compare",
      category: "dimensionnement",
      texte:    `${sol.nom} en Zone ${zone} sur ${typeSol} — SF calculé vs SF minimum requis EC7 DA2* : écart estimé ?`,
    },
  ];
}

function questionsEcartee(sol, donneesProjet, diag) {
  const { typeSol, h, zone } = getCtx(donneesProjet);
  const rang1    = getRang1(diag);
  const rang1Nom = rang1?.nom || "la solution recommandée";
  const regle    = sol.regles?.[0] || null;

  if (regle) {
    return [
      {
        id:       "pourquoi_ecartee",
        category: "exclusion",
        texte:    `Pourquoi ${sol.nom} est-elle écartée par la règle ${regle} sur ce projet — critère déclenchant ?`,
      },
      {
        id:       "regle_explication",
        category: "exclusion",
        texte:    `Que signifie la règle ${regle} et sur quelle norme (NF P94-261 §4.2 ou autre) repose-t-elle ?`,
      },
      {
        id:       "conditions_reevaluation",
        category: "exclusion",
        texte:    `Dans quelles conditions ${sol.nom} redeviendrait-elle pertinente sur ce type de projet — modification requise ?`,
      },
      {
        id:       "vs_adaptee",
        category: "comparaison",
        texte:    `Pourquoi ${sol.nom} est-elle écartée alors que ${rang1Nom} est très adaptée — différence de contexte sol/portance ?`,
      },
      {
        id:       "norme",
        category: "exclusion",
        texte:    `Quelle référence normative justifie l'éviction de ${sol.nom} sur sol portant (${typeSol}) en Zone ${zone} ?`,
      },
    ];
  }

  // Dégradation générique — pas de règle connue
  return [
    {
      id:       "pourquoi_ecartee_generique",
      category: "exclusion",
      texte:    `Pourquoi ${sol.nom} n'est-elle pas retenue dans le diagnostic de ce projet sur ${typeSol} H=${h}m ?`,
    },
    {
      id:       "alternatives",
      category: "comparaison",
      texte:    `Quelles solutions sont préférées à ${sol.nom} dans ce contexte et pour quelles raisons ?`,
    },
    {
      id:       "contexte_general",
      category: "contexte",
      texte:    `Quelles caractéristiques du projet (sol ${typeSol}, Zone ${zone}) rendent ${sol.nom} moins pertinente ?`,
    },
    {
      id:       "reevaluation",
      category: "exclusion",
      texte:    `Dans quelles conditions de sol ou de contrainte ${sol.nom} serait-elle pertinente pour un ouvrage H=${h}m ?`,
    },
    {
      id:       "modules_alternatives",
      category: "modules",
      texte:    `Quels modules GEO5 sont utilisés pour les solutions retenues à la place de ${sol.nom} ?`,
    },
  ];
}

// ─── Export principal ────────────────────────────────────────────────────────

/**
 * Génère 5 questions adaptatives pour la solution sélectionnée.
 * Read-only sur diagnosticBackend — ne recalcule pas le classement.
 *
 * @param {string} solutionId
 * @param {object} diagnosticBackend
 * @param {object} donneesProjet
 * @returns {Array<{id: string, texte: string, category: string}>}
 */
export function genererQuestionsAdaptatives(solutionId, diagnosticBackend, donneesProjet) {
  if (!solutionId || !diagnosticBackend || !donneesProjet) return [];

  const solution = chercherSolution(solutionId, diagnosticBackend);
  if (!solution) return [];

  switch (solution.niveau) {
    case "TRÈS_ADAPTÉE":
      return questionsTresAdaptee(solution, donneesProjet, diagnosticBackend);
    case "ADAPTÉE":
      return questionsAdaptee(solution, donneesProjet, diagnosticBackend);
    case "ADAPTÉE_AVEC_RÉSERVES":
      return questionsAvecReserves(solution, donneesProjet, diagnosticBackend);
    case "COMPLÉMENTAIRE":
      return questionsComplementaire(solution, donneesProjet, diagnosticBackend);
    case "PEU_ADAPTÉE":
      return questionsPeuAdaptee(solution, donneesProjet, diagnosticBackend);
    default:
      return questionsEcartee(solution, donneesProjet, diagnosticBackend);
  }
}
